import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { UserPlus, Save, Trash2 } from "lucide-react";
import { SIGNER_META } from "@/components/contracts/contractUtils";

function PersonSelect({ role, options, value, onChange, disabled }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-1" style={{ color: SIGNER_META[role].color }}>{SIGNER_META[role].label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="w-full px-3 py-2 text-sm disabled:opacity-60" data-testid={`signer-select-${role}`}>
        <option value="">— Select —</option>
        {options.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
    </label>
  );
}

export default function SignersPanel({ contract, onSave, editable }) {
  const [people, setPeople] = useState({ hosts: [], founders: [] });
  const s = contract.signers || {};
  const [sel, setSel] = useState({ founder: s.founder?.user_id, host: s.host?.user_id, cohost: s.cohost?.user_id });
  const [showCo, setShowCo] = useState(!!s.cohost);
  useEffect(() => { api.get("/contracts/people").then(({ data }) => setPeople(data)).catch(() => {}); }, []);
  useEffect(() => { setSel({ founder: s.founder?.user_id, host: s.host?.user_id, cohost: s.cohost?.user_id }); setShowCo(!!s.cohost); }, [contract.signers]); // eslint-disable-line react-hooks/exhaustive-deps
  const signed = (r) => !!s[r]?.signed_at;
  const dirty = ["founder", "host", "cohost"].some((r) => (sel[r] || "") !== (s[r]?.user_id || ""));

  return (
    <div className="surface-card p-5 space-y-3" data-testid="signers-panel">
      <div className="label-eyebrow">▮ SIGNERS</div>
      <PersonSelect role="founder" options={people.founders} value={sel.founder} onChange={(v) => setSel({ ...sel, founder: v })} disabled={!editable || signed("founder")} />
      <PersonSelect role="host" options={people.hosts} value={sel.host} onChange={(v) => setSel({ ...sel, host: v })} disabled={!editable || signed("host")} />
      {showCo ? (
        <div className="flex items-end gap-2">
          <div className="flex-1"><PersonSelect role="cohost" options={people.hosts} value={sel.cohost} onChange={(v) => setSel({ ...sel, cohost: v })} disabled={!editable || signed("cohost")} /></div>
          {editable && !signed("cohost") && (
            <button onClick={() => (s.cohost ? onSave({ remove_cohost: true }) : (setShowCo(false), setSel({ ...sel, cohost: "" })))} className="p-2 text-[var(--ff-red)]" title="Remove co-host" data-testid="signer-remove-cohost"><Trash2 size={14} /></button>
          )}
        </div>
      ) : editable && (
        <button onClick={() => setShowCo(true)} className="btn-ghost inline-flex items-center gap-1.5 text-xs w-full justify-center" data-testid="signer-add-cohost"><UserPlus size={12} /> Add Co-Host</button>
      )}
      {editable && dirty && (
        <button onClick={() => onSave({ founder_user_id: sel.founder || null, host_user_id: sel.host || null, cohost_user_id: sel.cohost || null })} className="btn-primary text-xs w-full justify-center" data-testid="signers-save-btn">
          <Save size={12} /> Save Signers
        </button>
      )}
      <ul className="text-xs space-y-1 pt-1">
        {Object.entries(s).map(([r, v]) => (
          <li key={r} className="flex justify-between" data-testid={`signer-status-${r}`}>
            <span style={{ color: SIGNER_META[r]?.color }}>{SIGNER_META[r]?.label}: {v.name}</span>
            <span className={v.signed_at ? "text-emerald-400" : "text-[var(--ff-text-dim)]"}>{v.signed_at ? `Signed ${new Date(v.signed_at).toLocaleDateString()}` : "Pending"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
